import { KmsTypePublishCategory, KmsTypeCategoryMembership } from "@kaltura/mediaspace-shared-types";
import { addBaseUrl, encodeEntryNameForURI, translate } from "@kaltura/mediaspace-shared-utils";

export enum EnrichmentType {
    label = "label",
    url = "url",
    all = "all",
}

export enum CategoryType {
    channel = "channel",
    gallery = "gallery",
    externalCategory = "externalCategory",
}

export enum SaveType {
    save = "save",
    crossApplicationUnpublish = "crossApplicationUnpublish",
}

export const galleryLabel = (gallery: KmsTypePublishCategory) =>
    translate("%1 (Gallery ID: %2)", [gallery.name, gallery.id]);

export const channelLabel = (channel: KmsTypePublishCategory, isKAF?: boolean) => {
    if (!isKAF) {
        // keeping original format for KMS
        return translate("%1 (Channel ID: %2)", [channel.name, channel.id]);
    }
    // For KAF: display additional details about course and category IDs to distinguish between Shared, Created, and Not Created courses.
// "Not Created" refers to a course that exists in the LMS but has not yet been created in the Kaltura system.

    const idStr = String(channel.id);

    // Shared repository channels do not have course/category IDs , Just return the channel name
    if (channel.membership === KmsTypeCategoryMembership.SHARED) {
        return channel.name;
    }

    // If id indicates not created yet, decode course id and show N/A for Category ID
    const decodedCourseId = extractCourseIdFromNotCreated(idStr);
    if (decodedCourseId) {
        return translate("%1 (Course ID: %2, Category ID: N/A)", [channel.name, decodedCourseId]);
    }

    const courseId = channel.fullName?.split(">")?.pop() || "N/A";
    // If courseId is missing, show N/A for Course ID
    return translate("%1 (Course ID: %2, Category ID: %3)", [channel.name, courseId, idStr || "N/A"]);
};

// Generates a URL for the given category based on its type
const getCategoryUrl = (category: KmsTypePublishCategory, categoryType: CategoryType) => {
    switch (categoryType) {
        case CategoryType.channel:
            return addBaseUrl(`/channel/${encodeEntryNameForURI(category.name)}/${category.id}`);
        case CategoryType.gallery:
            return addBaseUrl(`/category/${encodeEntryNameForURI(category.name)}/${category.id}`);
        case CategoryType.externalCategory:
            // external category will never have url
            return undefined;
        default:
            return undefined;
    }
};

function extractCourseIdFromNotCreated(id: string): string | null {
    const match = id.match(/^notcreatedyetcat_([^_]+)_/i);
    if (!match || !match[1]) return null;
    try {
        return atob(match[1]);
    }
    catch {
        return null;
    }
}

// Generates a label for the given category based on its type
const getCategoryLabel = (category: KmsTypePublishCategory, categoryType: CategoryType) => {
    switch (categoryType) {
        case CategoryType.channel:
            return channelLabel(category);
        case CategoryType.gallery:
            return galleryLabel(category);
        case CategoryType.externalCategory:
            return translate("Category ID: %1", [category.id]);
        default:
            return undefined;
    }
};

// Enriches the given categories with labels and/or URLs based on the enrichment type
export const enrichCategoriesData = (
    categories: KmsTypePublishCategory[],
    categoryType: CategoryType,
    enrichmentType: EnrichmentType
) => {
    const shouldEnrichWithLabel = enrichmentType === EnrichmentType.label || enrichmentType === EnrichmentType.all;
    const shouldEnrichWithUrl = enrichmentType === EnrichmentType.url || enrichmentType === EnrichmentType.all;
    return categories.map((category) => ({
        ...category,
        url: shouldEnrichWithUrl ? getCategoryUrl(category, categoryType) : undefined,
        label: shouldEnrichWithLabel ? getCategoryLabel(category, categoryType) : undefined,
    }));
};

// Enriches the given channels and galleries with labels and/or URLs and combines them into a single array
export const enrichAndCombineCategories = (
    channels: KmsTypePublishCategory[],
    galleries: KmsTypePublishCategory[],
    enrichmentType: EnrichmentType
) => {
    // enrich channel with label, url or both
    const channelLocations = enrichCategoriesData(channels, CategoryType.channel, enrichmentType);
    // enrich gallery with label, url or both
    const galleryLocations = enrichCategoriesData(galleries, CategoryType.gallery, enrichmentType);
    // concat the channels and galleries
    return [...galleryLocations, ...channelLocations];
};
